import { FC, useCallback, useMemo } from 'react';

import {
  ManagerData,
  OnboardingData,
  OnboardingDataStatus,
} from 'api/types/data';
import {
  ClientAccountStatusType,
  CountryEntity,
  LegalClientOnboardingStatusType,
  OnboardingStatusType,
} from 'api/types/entity';
import { LegalClientInnerItemEntity } from 'api/types/entity/legal/legalClientInnerItemEntity';
import { DialogConfirmDelete } from 'components';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { ClientFormContent } from 'modules/client/common/components';
import { useLockAndUnlockReadOnly } from 'modules/client/common/hooks';

import { useTranslation } from '../../../../../../libs/i18n';
import { ActionProps } from '../../../../common/components/ClientFormContent/types';
import { renderClientStatus } from '../../../../common/helpers';
import { useFormContext } from '../../../forms/legalClientForm';
import {
  LegalClientSections,
  LegalClientSectionsProps,
} from '../../LegalClientSections';

export interface FormContentProps extends LegalClientSectionsProps {
  countriesLoading?: boolean;
  countries: CountryEntity[];
  heads: LegalClientInnerItemEntity[];
  legalHeads: LegalClientInnerItemEntity[];
  branches: LegalClientInnerItemEntity[];
  submitLoading?: boolean;
  managers: ManagerData[];
  managersLoading?: boolean;
  representativeOnboardingData?: OnboardingData;
  currentAccountStatus: ClientAccountStatusType | null;
  currentOnboardingStatus: LegalClientOnboardingStatusType;
  visibleOnboardingStatuses?: OnboardingDataStatus[];
  onChangeOnboardingStatus?: () => void;
  onChangeAccountStatus?: () => void;
  onShowVersionHistory?: () => void;
  onLinkToBright?: () => void;
  onDownloadLegalClientQuestionnaire?: () => void;
  deleteLegalClientLoading?: boolean;
  onDeleteLegalClient?: () => void;
}

export const FormContent: FC<FormContentProps> = ({
  submitLoading,
  currentOnboardingStatus,
  representativeOnboardingData,
  currentAccountStatus,
  lockClientIfPossible,
  unlockClient,
  onChangeOnboardingStatus,
  onChangeAccountStatus,
  onShowVersionHistory,
  onLinkToBright,
  onDownloadLegalClientQuestionnaire,
  deleteLegalClientLoading,
  onDeleteLegalClient,
  ...sectionsProps
}) => {
  const { resetForm, initialValues, setReadOnly } = useFormContext();

  const {
    isOpen: isOpenConfirmDelete,
    onClose: onCloseConfirmDelete,
    open: openConfirmDelete,
  } = useDialog();

  const { fullName } = initialValues.companyName;

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );
  const { t } = useTranslation();

  const topContent = useMemo(
    () =>
      renderClientStatus(
        {
          onboarding: representativeOnboardingData,
          status: currentAccountStatus || currentOnboardingStatus,
        },
        t,
        { size: 's' }
      ),
    [
      currentAccountStatus,
      currentOnboardingStatus,
      representativeOnboardingData,
      t,
    ]
  );

  const handleDelete = useCallback(() => {
    openConfirmDelete();
  }, [openConfirmDelete]);

  const actionMenuProps = useMemo<ActionProps>(
    () => ({
      onChangeAccountStatus:
        currentOnboardingStatus === OnboardingStatusType.AccountOpened &&
        currentAccountStatus !== ClientAccountStatusType.Closed
          ? onChangeAccountStatus
          : undefined,
      onChangeOnboardingStatus:
        currentOnboardingStatus !== OnboardingStatusType.AccountOpened
          ? onChangeOnboardingStatus
          : undefined,
      onDownloadClientQuestionnaire: onDownloadLegalClientQuestionnaire,
      onDelete: onDeleteLegalClient ? handleDelete : undefined,
      onLinkToBright,
    }),
    [
      currentOnboardingStatus,
      currentAccountStatus,
      onChangeAccountStatus,
      onChangeOnboardingStatus,
      onDownloadLegalClientQuestionnaire,
      onDeleteLegalClient,
      handleDelete,
      onLinkToBright,
    ]
  );

  return (
    <>
      <ClientFormContent
        shownTabBar
        actionMenuProps={actionMenuProps}
        content={<LegalClientSections {...sectionsProps} />}
        headerAfterContent={topContent}
        headerTitle={fullName}
        resetForm={resetForm}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      {onDeleteLegalClient && (
        <DialogConfirmDelete
          isOpen={isOpenConfirmDelete}
          loading={deleteLegalClientLoading}
          onClose={onCloseConfirmDelete}
          onConfirm={onDeleteLegalClient}
        />
      )}
    </>
  );
};
